import { makeStyles } from '@material-ui/core/styles';
import React, { forwardRef, Ref, useContext, useMemo } from 'react';
import {
  TableFieldRowProps,
  TableFieldContext,
  TableFieldCells,
  usePrompts,
  PromptContext,
} from '@eas/common-web';
import { get, cloneDeep } from 'lodash';
import { SubjectRelationship } from '../subjects-types';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import Typography from '@material-ui/core/Typography';
import {
  UserRoleInstance,
  SepnoDetails as SepnoDetailsType,
} from '../../../models';
import { SepnoDetails } from '../../role-instance/fields/sepno-details-fields';
import Button from '@material-ui/core/Button';

const useStyles = makeStyles((theme) => ({
  rowWrapper: {
    background: '#e0e2e3',
    '&:hover': {
      backgroundColor: theme.palette.highlight,
    },
  },
  row: {
    boxSizing: 'border-box',
    minWidth: '100%',
    minHeight: 30,
    padding: '2px 16px',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  rowDetails: {
    width: '100%',
    padding: '2px 16px',
    borderBottom: '1px solid #cdcdcd',
  },
  tableRowActions: {
    flexShrink: 0,
    width: 35,
    height: 20,
    verticalAlign: 'top',
    display: 'inline-block',
    padding: 0,
    overflow: 'hidden',
    '& svg': {
      cursor: 'pointer',
      width: 20,
      height: 20,
      padding: '2pt',
    },
  },
  cellWrapper: {
    flexShrink: 0,
    display: 'flex',
    paddingRight: 10,
    boxSizing: 'border-box',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '11px',
    fontWeight: 600,
    marginLeft: 10,
  },
  content: {
    display: 'flex',
  },
  icon: {
    float: 'left',
    marginTop: -4,
    marginRight: 10,
  },
  text: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  radioButton: {
    padding: 0,
  },
}));

export const UserRelationshipTableFieldRow = forwardRef(
  function UsersTableFieldRow(
    { index, value }: TableFieldRowProps<SubjectRelationship>,
    ref: Ref<HTMLDivElement>
  ) {
    const classes = useStyles();
    const { filteredColumns } = useContext<
      TableFieldContext<SubjectRelationship>
    >(TableFieldContext);

    const roleInstances = useMemo(() => {
      const enhancedEmpoweredRoleInstances = (
        value?.empoweredUserRoles ?? []
      ).map((roleInstance) => {
        const enhanced = cloneDeep(roleInstance);
        enhanced.role.name = `[Z] ${roleInstance.role.name}`;

        return enhanced;
      });

      const roleInstances = [
        ...(value?.userRoleInstances ?? []),
        ...enhancedEmpoweredRoleInstances,
      ];

      const sortedRoleInstances = sortRoleInstances(roleInstances);

      return sortedRoleInstances;
    }, [value?.empoweredUserRoles, value?.userRoleInstances]);

    usePrompts(
      roleInstances.map((r) => ({
        key: `SEPNO_PROMPT-${r.id}`,
        dialogText: '',
        dialogTitle: 'Detail SEPNO',
        dialogShowConfirm: false,
        FormFields: () => SepnoDetails({ disabled: true }),
        formInitialValues: r,
        dialogWidth: 800,
      }))
    );

    const { testPrompt } = useContext(PromptContext);

    return (
      <div ref={ref} className={classes.rowWrapper}>
        <div className={classes.row}>
          {filteredColumns.map((column, i) => {
            const { CellComponent = TableFieldCells.TextCell } = column;
            return (
              <div
                key={i}
                className={classes.cellWrapper}
                style={{
                  width: column.width,
                  color: value?.active ? 'inherit' : 'gray',
                }}
              >
                <CellComponent
                  index={index}
                  value={get(value, column.datakey, '')}
                  rowValue={value}
                  column={column as any}
                />
              </div>
            );
          })}
        </div>
        <div className={classes.rowDetails}>
          {roleInstances.map((roleInstance, i) => {
            const sepnoDetails = formatSepnoDetailValues(roleInstance);
            return (
              <div key={i} className={classes.cellWrapper}>
                <div
                  style={{
                    width: filteredColumns[0].width,
                    color: roleInstance?.active ? 'inherit' : 'gray',
                  }}
                  key={roleInstance.id}
                >
                  <Typography variant="body1" component="div">
                    <ul
                      style={{
                        width: filteredColumns[0].width,
                      }}
                    >
                      <span
                        className={classes.label}
                        key={roleInstance.role.name}
                      >
                        <SubdirectoryArrowRightIcon
                          fontSize="small"
                          className={classes.icon}
                        />
                        <span className={classes.text}>
                          {roleInstance.role.name} (
                          {roleInstance.role.registeredFor?.name})
                        </span>
                      </span>
                    </ul>
                  </Typography>
                </div>
                <div
                  style={{
                    width: filteredColumns[1].width,
                    color: roleInstance?.active ? 'inherit' : 'gray',
                  }}
                >
                  <TableFieldCells.DateTimeCell
                    index={index}
                    column={{} as any}
                    value={get(roleInstance, 'validFrom', '')}
                    rowValue={value}
                  />
                </div>
                <div
                  style={{
                    width: filteredColumns[2].width,
                    color: roleInstance?.active ? 'inherit' : 'gray',
                  }}
                >
                  <TableFieldCells.DateTimeCell
                    index={index}
                    column={{} as any}
                    value={get(roleInstance, 'validTo', '')}
                    rowValue={value}
                  />
                </div>
                <div
                  style={{
                    width: filteredColumns[3].width,
                    color: roleInstance?.active ? 'inherit' : 'gray',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  {sepnoDetails && (
                    <Button
                      style={{ marginLeft: 10, padding: 0 }}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        testPrompt({
                          key: `SEPNO_PROMPT-${roleInstance.id}`,
                          submitCallback: () => {},
                        });
                      }}
                    >
                      Detail
                    </Button>
                  )}
                  <div style={{ maxWidth: 300, marginLeft: 8 }}>
                    <TableFieldCells.TextCell
                      index={index}
                      column={{} as any}
                      value={sepnoDetails}
                      rowValue={value}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

export const sortRoleInstances = (roleInstances: UserRoleInstance[]) =>
  roleInstances?.sort((firstRole, secondRole) => {
    if (
      firstRole!.role.registeredFor!.name < secondRole!.role.registeredFor!.name
    ) {
      return -1;
    }
    if (
      firstRole!.role.registeredFor!.name > secondRole!.role.registeredFor!.name
    ) {
      return 1;
    }
    return 0;
  });

export const formatSepnoDetailValues = (roleInstance: UserRoleInstance) => {
  const sepnoDetails = get(
    roleInstance,
    'sepnoDetails',
    ''
  ) as SepnoDetailsType[];
  if (!sepnoDetails || sepnoDetails.length === 0) {
    return undefined;
  }
  const result = sepnoDetails
    .map((d) => {
      const typeLabel = d?.identifier?.type === 'ICZ' ? 'IČZ ' : 'IČP';
      const strIdentifier = `${typeLabel}${d?.identifier?.value}`;
      const from = d.validFrom
        ? new Date(d.validFrom).toLocaleDateString('cs-CZ')
        : undefined;
      const to = d.validTo
        ? new Date(d.validTo).toLocaleDateString('cs-CZ')
        : undefined;
      const dates = !from && !to ? '' : `(${from ?? '?'} - ${to ?? '?'})`;
      return `${strIdentifier} ${dates}`;
    })
    .join(', ');
  return result;
};
